import React, { useEffect, useRef, useState } from 'react';

import { observer } from 'mobx-react';
import classNames from 'classnames';
import { AutoSizer, List, ListRowProps } from 'react-virtualized';
import { RenderedRows } from 'react-virtualized/dist/es/List';
import { usePrevious } from '@bringg-frontend/hooks';

import { useRowHover, useScroll } from '../../../hooks';
import { DetailedColumn, ColumnDataBasic } from '../../../types/column-detailed-types';
import ScrollBar from '../scroll-bar/scroll-bar';
import stylesMixins from '../../../styles/mixins.module.scss';

import styles from './detail-column.module.scss';

const ROW_HEIGHT = +stylesMixins.lineHight || 32;
const MIN_WIDTH_TO_KEEP_SCROLL_SYNC = 1;

export type CustomStyle = { root?: string; header?: string; body?: string; group?: string };
interface Props<T extends ColumnDataBasic, G extends ColumnDataBasic | void = void> {
	column: DetailedColumn<T, G>;
	columnIndex?: number;
	rowData: (T | G)[];
	rowCount: number;
	onRowsRendered?: (info: RenderedRows) => void;
	customStyles?: CustomStyle;
	idPrefix?: string;
	['data-test-id']?: string;
	withScrollBar?: boolean;
	isGroupFn?: (row: T | G) => boolean;
}

export const DETAILED_LIST_PREFIX_ID = 'detailed-list-';

const RenderGroup = <T extends ColumnDataBasic, G extends ColumnDataBasic | void>({
	row,
	className,
	column,
	style
}: {
	row: G;
	className?: string;
	column: DetailedColumn<T, G>;
	style: React.CSSProperties;
}) => {
	const ref = useRef<HTMLDivElement>(null);
	const rowId = (row as ColumnDataBasic).id;

	useRowHover(ref, rowId);

	const { Group } = column;

	const isGroup = typeof Group === 'function' || typeof Group === 'object';

	return (
		<div
			role="row"
			ref={ref}
			data-test-id={`detailed-row-${rowId}`}
			className={classNames(styles.tr, className, `detailed-row-${rowId}`)}
			style={style}
		>
			<div role="group" className={styles.td} data-test-id={`detailed-cell-${rowId}`}>
				{isGroup && <Group row={row} />}
			</div>
		</div>
	);
};

const RenderRow = <T extends ColumnDataBasic, G extends ColumnDataBasic | void>({
	row,
	column,
	style
}: {
	row: T;
	column: DetailedColumn<T, G>;
	style: React.CSSProperties;
}) => {
	const ref = useRef<HTMLDivElement>(null);
	useRowHover(ref, row.id);

	const { accessor, Cell } = column;

	const value = typeof accessor === 'function' ? accessor(row) : accessor !== undefined ? row[accessor] : undefined;

	const isCell = typeof Cell === 'function' || typeof Cell === 'object';

	return (
		<div
			role="row"
			ref={ref}
			data-test-id={`detailed-row-${row.id}`}
			className={classNames(styles.tr, `detailed-row-${row.id}`)}
			style={style}
		>
			<div role="cell" className={styles.td} data-test-id={`detailed-cell-${row.id}`}>
				<div className={styles.tdInner}>{isCell ? <Cell row={row} value={value} /> : value}</div>
			</div>
		</div>
	);
};

const DetailColumn = <T extends ColumnDataBasic, G extends ColumnDataBasic | void = void>({
	column,
	columnIndex,
	rowData,
	rowCount,
	onRowsRendered,
	customStyles,
	idPrefix,
	'data-test-id': dataTestId,
	withScrollBar,
	isGroupFn,
	listRef
}: Props<T, G> & { listRef?: React.Ref<List> }) => {
	const listContainerRef = useRef<HTMLDivElement>();
	const [innerScrollRef, setInnerScrollRef] = useState<HTMLDivElement | undefined>(undefined);

	const listId = `${idPrefix || DETAILED_LIST_PREFIX_ID}${column.id}`;
	const lastCount = usePrevious(rowCount);

	useEffect(() => {
		listContainerRef.current = document.getElementById(listId) as HTMLDivElement;
	}, []);

	useEffect(() => {
		if ((listContainerRef.current && !innerScrollRef) || lastCount === 0) {
			if (listContainerRef.current?.scrollHeight) {
				const innerScrollElement = listContainerRef.current?.querySelector(
					'.ReactVirtualized__Grid__innerScrollContainer'
				);
				if (innerScrollElement) {
					setInnerScrollRef(innerScrollElement as HTMLDivElement);
				}
			}
		}
	}, [listContainerRef, innerScrollRef, lastCount]);

	useScroll(innerScrollRef, listContainerRef.current, columnIndex);

	const renderRow = ({ index, style, key }: ListRowProps) => {
		const row = rowData[index];

		if (!row) return <div key={key} style={style} />;

		if (isGroupFn?.(row)) {
			return (
				<RenderGroup key={key} row={row as G} column={column} style={style} className={customStyles?.group} />
			);
		}

		return <RenderRow key={key} row={row as T} column={column} style={style} />;
	};

	return (
		<div className={classNames(styles.detailTable, customStyles?.root)} data-test-id={dataTestId} role="table">
			<div className={classNames(styles.columnHeader, customStyles?.header)}>
				<div className={styles.tr} role="row">
					<div className={styles.th} role="columnheader">
						<div className={styles.thInner}>{column.Header}</div>
					</div>
				</div>
			</div>

			<div className={classNames(styles.columnBody, customStyles?.body)} role="rowgroup">
				<AutoSizer>
					{({ height, width }) => (
						<div style={{ display: 'flex', width: width === 0 ? 1 : width }}>
							<List
								id={listId}
								ref={listRef}
								rowRenderer={width !== 0 ? renderRow : () => ''}
								onRowsRendered={onRowsRendered}
								rowCount={rowCount}
								height={height}
								width={width === 0 ? MIN_WIDTH_TO_KEEP_SCROLL_SYNC : width}
								rowHeight={ROW_HEIGHT}
								className={styles.list}
							/>
							<ScrollBar
								height={height}
								scrollHeight={rowCount * ROW_HEIGHT}
								showScrollBar={withScrollBar}
							/>
						</div>
					)}
				</AutoSizer>
			</div>
		</div>
	);
};

export default observer(DetailColumn);
